import { QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTableStoreContext } from '@/components/table/contexts/TableContext.tsx';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { PagedDto } from '@/components/table/types/PagedDto.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { serializeFilters } from '@/components/table/types/filterSerialization.ts';
import { RowWithId } from '@/components/table/types/RowWithId.ts';

/**
 * Generyk, nie `PagedDto<unknown>` — ten drugi wygrywał wnioskowanie
 * `useInfiniteQuery` i wiersze wracały jako `unknown[]`.
 */
export const getNextPageParam = <T>(
  lastPage: PagedDto<T>,
): number | undefined => {
  // Strona o zerowym rozmiarze nigdy nie dowiozłaby kolejnych elementów,
  // a `loaded` stałoby w miejscu — pytanie o następną stronę kręciłoby się bez końca.
  if (lastPage.pageSize <= 0) return undefined;
  const loaded = (lastPage.currentPage + 1) * lastPage.pageSize;
  return loaded < lastPage.totalElements ? lastPage.currentPage + 1 : undefined;
};

/**
 * Backend tnie strony po offsecie, więc remis w kolumnie sortującej potrafi
 * powtórzyć ten sam wiersz na kolejnej stronie.
 */
export const dedupeById = <T extends RowWithId>(rows: T[]): T[] => {
  const seen = new Set<string>();
  return rows.filter((row) => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
};

/**
 * Wersja `useTableData` dla listy mobilnej: strony dokładają się do jednej
 * listy, więc numer strony nie może siedzieć w kluczu zapytania — zmiana
 * filtrów lub szukania zeruje listę, przewinięcie tylko dokłada.
 */
const useInfiniteTableData = <T extends RowWithId>({
  queryKey,
  fetchData,
}: {
  queryKey: QueryKey;
  fetchData: (
    pageRequest: PageRequest,
    filterCriteria: FilterCriteriaDto,
  ) => Promise<PagedDto<T>>;
}) => {
  const filterCriteria = useTableStoreContext((state) => state.filterCriteria);
  const pageRequest = useTableStoreContext((state) => state.pageRequest);
  const searchTerm = useTableStoreContext((state) => state.searchTerm);
  const quickFilters = useTableStoreContext((state) => state.quickFilterState);
  const tableDefinition = useTableStoreContext(
    (state) => state.tableDefinition,
  );

  const isInitialized = tableDefinition?.fields?.length > 0;

  const filterCriteriaDto: FilterCriteriaDto = {
    combinator: filterCriteria.combinator,
    filters: serializeFilters(filterCriteria.filters),
    searchTerm: searchTerm.length >= 2 ? searchTerm : '',
    quickFilters: quickFilters,
  };

  const { size, sortByFieldId, sortDirection } = pageRequest;

  const query = useInfiniteQuery({
    queryKey: [
      ...queryKey,
      'infinite',
      { size, sortByFieldId, sortDirection },
      filterCriteria,
      searchTerm,
      quickFilters,
    ],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchData(
        { page: pageParam, size, sortByFieldId, sortDirection },
        filterCriteriaDto,
      ),
    getNextPageParam,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: isInitialized,
  });

  // Tożsamość musi być stabilna: `IntersectionObserver` listy mobilnej trzyma ją
  // w zależnościach efektu i inaczej przepinałby wartownika przy każdym renderze.
  const { fetchNextPage } = query;
  const fetchNextPageStable = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const rows = useMemo(
    () => dedupeById(query.data?.pages.flatMap((page) => page.content) ?? []),
    [query.data],
  );

  return {
    rows,
    totalElements: query.data?.pages[0]?.totalElements ?? 0,
    // `isPending`, nie `isLoading`: dopóki definicja tabeli nie wróci, zapytanie
    // jest wyłączone i `isLoading` już jest fałszem — pustka mignęłaby przed
    // pierwszym żądaniem.
    isPending: query.isPending,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: fetchNextPageStable,
  };
};

export default useInfiniteTableData;
